import React from 'react';
import { Plus, Search, X } from 'lucide-react';

import { adminContent } from 'services/api';
import type {
  AdminCatalogPage,
  ModuleLibraryRow,
  ModuleLibrarySearchParams,
  ModuleLibraryType,
  ModuleLibraryView,
  ModuleLibraryViewCounts,
  QuestionLibraryCatalogCourse,
} from 'services/api/adminContentClient';
import { CoursePicker } from 'components/admin/CoursePicker';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { cn } from 'lib/utils';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const UNASSIGNED = 'unassigned';

export const MODULE_LIBRARY_VIEWS: Array<{ value: ModuleLibraryView; label: string }> = [
  { value: 'all', label: 'All modules' },
  { value: 'unassigned', label: 'Not in any course' },
  { value: 'shared', label: 'In 2+ courses' },
  { value: 'empty', label: 'No questions' },
  { value: 'description_is_name', label: 'Description is just the name' },
  { value: 'recent', label: 'Edited in the last 7 days' },
];

export const MODULE_TYPE_OPTIONS: Array<{ value: ModuleLibraryType; label: string }> = [
  { value: 'standard', label: 'Standard' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'roleplay', label: 'Roleplay' },
  { value: 'voice', label: 'Voice mode' },
];

const moduleTypeLabel = (row: ModuleLibraryRow) => (row.is_quiz ? 'Quiz' : row.is_roleplay ? 'Roleplay' : 'Standard');

export type ModuleSearchPanelProps = {
  selectedFlowId: number | null;
  refreshToken: number;
  courses: QuestionLibraryCatalogCourse[];
  onSelectModule: (flowId: number) => void;
  onCreateModule: () => void;
};

const useDebouncedValue = <T,>(value: T, delayMs: number) => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [delayMs, value]);
  return debounced;
};

/** Module Builder's All modules tab: saved views, filters and a paged table, like the Question Library's search. */
export const ModuleSearchPanel: React.FC<ModuleSearchPanelProps> = ({
  selectedFlowId,
  refreshToken,
  courses,
  onSelectModule,
  onCreateModule,
}) => {
  const [view, setView] = React.useState<ModuleLibraryView>('all');
  const [searchInput, setSearchInput] = React.useState('');
  const search = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const [moduleType, setModuleType] = React.useState<'all' | ModuleLibraryType>('all');
  const [courseId, setCourseId] = React.useState('all');
  const [page, setPage] = React.useState(1);
  const [rows, setRows] = React.useState<ModuleLibraryRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [counts, setCounts] = React.useState<ModuleLibraryViewCounts | null>(null);

  const baseParams = React.useMemo(() => {
    // Inactive modules stay findable here, as they were in the old list (rows are marked Inactive).
    const params: Omit<ModuleLibrarySearchParams, 'view' | 'page' | 'page_size'> = { include_inactive: true };
    if (search) params.search = search;
    if (moduleType !== 'all') params.type = moduleType;
    if (courseId === UNASSIGNED) params.course_id = UNASSIGNED;
    else if (courseId !== 'all') params.course_id = Number(courseId);
    return params;
  }, [courseId, moduleType, search]);

  React.useEffect(() => {
    setPage(1);
  }, [baseParams, view]);

  React.useEffect(() => {
    let cancelled = false;
    adminContent.flows
      .viewCounts(baseParams)
      .then((response) => {
        if (!cancelled) setCounts(response.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, refreshToken]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminContent.flows
      .search({ ...baseParams, view, page, page_size: PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        const data = response.data as AdminCatalogPage<ModuleLibraryRow>;
        const results = Array.isArray(data?.results) ? data.results : [];
        setRows(results);
        setTotal(typeof data?.count === 'number' ? data.count : results.length);
        setError(null);
      })
      .catch((loadError: unknown) => {
        if (cancelled) return;
        const status = (loadError as { response?: { status?: number } })?.response?.status;
        if (status === 404 && page > 1) {
          // The page no longer exists (for example, a save removed the last row from this view): step back.
          setPage((current) => Math.max(1, current - 1));
          return;
        }
        setRows([]);
        setTotal(0);
        setError('Could not load modules.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, page, refreshToken, view]);

  const courseOptions = React.useMemo(
    () => [
      { value: UNASSIGNED, label: 'Not in any course' },
      ...courses
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
        .map((course) => ({ value: String(course.id), label: course.name })),
    ],
    [courses],
  );

  const hasFilters = !!searchInput.trim() || moduleType !== 'all' || courseId !== 'all';
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(total, page * PAGE_SIZE);

  return (
    <Card disableAnimation maxWidth="none" data-testid="fb-search-panel">
      <CardHeader>
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle title="Select a module" />
          <Button variant="secondary" onClick={onCreateModule} size="auto">
            <Plus className="h-4 w-4" aria-hidden="true" /> Create new module
          </Button>
        </div>
      </CardHeader>
      <CardContent variant="custom" className="space-y-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Saved views">
          {MODULE_LIBRARY_VIEWS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setView(option.value)}
              aria-pressed={view === option.value}
              data-testid={`fb-view-${option.value}`}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                view === option.value
                  ? 'border-foundation-base-darkbase-dark-500 bg-foundation-base-darkbase-dark-500 text-white'
                  : 'border-foundation-calm-blue-200 bg-white/70 text-foundation-base-darkbase-dark-500 hover:bg-white',
              )}
            >
              {option.label}
              {counts && typeof counts[option.value] === 'number' ? (
                <span className="ml-1.5 opacity-70">{counts[option.value].toLocaleString()}</span>
              ) : null}
            </button>
          ))}
        </div>

        <div className="grid gap-3 rounded-3xl bg-white/40 p-3 md:grid-cols-[minmax(14rem,2fr)_minmax(10rem,1fr)_minmax(12rem,1.2fr)]">
          <div className="space-y-1.5">
            <Label htmlFor="fb-search-input" className="text-xs uppercase text-muted-foreground">Search</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                id="fb-search-input"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Name, title, or ID"
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fb-search-type" className="text-xs uppercase text-muted-foreground">Module type</Label>
            <Select value={moduleType} onValueChange={(value) => setModuleType(value as 'all' | ModuleLibraryType)}>
              <SelectTrigger id="fb-search-type" className="w-full"><SelectValue placeholder="Module type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {MODULE_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fb-search-course" className="text-xs uppercase text-muted-foreground">Course</Label>
            <CoursePicker
              value={courseId}
              onValueChange={setCourseId}
              options={courseOptions}
              placeholder="Course"
              triggerId="fb-search-course"
              sentinelOption={{ value: 'all', label: 'All courses' }}
              emptyMessage="No courses found"
              maxVisibleOptions={50}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span data-testid="fb-search-total">
            {total.toLocaleString()} module{total === 1 ? '' : 's'}
            {hasFilters ? ' match' : ''}
          </span>
          {hasFilters ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-medium text-foundation-orange-500"
              onClick={() => {
                setSearchInput('');
                setModuleType('all');
                setCourseId('all');
              }}
            >
              <X className="h-3 w-3" aria-hidden="true" /> Clear filters
            </button>
          ) : null}
        </div>

        <div className="overflow-hidden rounded-3xl bg-white/40">
          {error ? <p role="alert" className="px-4 py-3 text-sm text-destructive">{error}</p> : null}
          <div className="max-h-[32rem] overflow-auto">
            <table className="w-full min-w-[48rem] table-fixed text-sm">
              <thead className="sticky top-0 z-10 bg-white/90 text-left text-xs uppercase text-muted-foreground backdrop-blur">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="w-28 px-4 py-2 font-medium">Type</th>
                  <th className="w-48 px-4 py-2 font-medium">Courses</th>
                  <th className="px-4 py-2 font-medium">Description</th>
                  <th className="w-24 px-4 py-2 text-right font-medium">Questions</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan={5}><div className="flex justify-center py-10"><LoadingAnimation /></div></td></tr>
                ) : null}
                {!loading && rows.length === 0 && !error ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No modules match.</td></tr>
                ) : null}
                {rows.map((row) => {
                  const selected = row.id === selectedFlowId;
                  const courseCount = row.courses?.length ?? 0;
                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        'cursor-pointer border-t border-black/5 hover:bg-white/50',
                        selected && 'bg-foundation-orange-50 hover:bg-foundation-orange-50',
                      )}
                      onClick={() => onSelectModule(row.id)}
                      aria-selected={selected}
                      data-testid={`fb-search-row-${row.id}`}
                    >
                      <td className="max-w-0 px-4 py-2">
                        <button
                          type="button"
                          className="block w-full min-w-0 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectModule(row.id);
                          }}
                          aria-label={`Edit ${row.name}`}
                        >
                          <span className="block truncate font-medium text-foreground">
                            {row.is_active === false ? (
                              <span className="mr-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10.5px] font-medium text-amber-800">Inactive</span>
                            ) : null}
                            {row.name}
                          </span>
                          {row.title && row.title.trim() !== row.name.trim() ? (
                            <span className="block truncate text-[11px] text-muted-foreground">{row.title}</span>
                          ) : null}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                        {moduleTypeLabel(row)}
                        {row.voice_mode_enabled ? <span className="ml-1 text-[11px]">· Voice</span> : null}
                      </td>
                      <td className="max-w-0 px-4 py-2 text-xs">
                        {courseCount ? (
                          <span className="block truncate">
                            {row.courses[0]?.name}
                            {courseCount > 1 ? <span className="text-muted-foreground"> +{courseCount - 1}</span> : null}
                          </span>
                        ) : (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">None</span>
                        )}
                      </td>
                      <td className="max-w-0 truncate px-4 py-2 text-xs text-muted-foreground">
                        {row.description?.trim() || '-'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-muted-foreground">{row.question_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-4 py-2 text-xs text-muted-foreground">
            <span data-testid="fb-search-range">Showing {start}–{end} of {total.toLocaleString()}</span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="auto" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}>Previous</Button>
              <span>Page {page} of {totalPages}</span>
              <Button variant="ghost" size="auto" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages}>Next</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ModuleSearchPanel;
